import { AccountInfo, AuthenticationResult, BrowserAuthError, InteractionRequiredAuthError, InteractionStatus, IPublicClientApplication } from '@azure/msal-browser';
import { SalesChannel } from '../dto/model';

let isInitialised = false;

export const GetAccessToken = async (instance: IPublicClientApplication, inProgress: InteractionStatus, scopes: string[]): Promise<AuthenticationResult> => {
  const accessTokenRequest = { scopes };

  if (!isInitialised) {
    await instance.initialize();
    isInitialised = true;
  }

  if (inProgress === InteractionStatus.None) {
    try {
      return await instance.acquireTokenSilent(accessTokenRequest);
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError || e instanceof BrowserAuthError) {
        await instance.acquireTokenRedirect(accessTokenRequest);
      }
      //we need this console log to help debug the error on prod
      console.log(e);
      if (e instanceof InteractionRequiredAuthError || e instanceof BrowserAuthError) {
        await instance.acquireTokenRedirect(accessTokenRequest);
      }
      throw e;
    }
  }
  throw new Error('Access token could not be acquired');
};

export enum Role {
  BookingManager = 'BookingManager',
  BookingAdmin = 'BookingAdministrator',
  Recorded = 'Recorded',
  CrmSalesRole = 'CrmBookingAgent',
  InboundAgent = 'InboundAgent',
  FieldSalesBookingAgent = 'FieldSalesBookingAgent',
  SalesOSBookingAgent = 'SalesOSBookingAgent',
  RevisionAgent = 'RevisionAgent',
  LiveReporter = 'LiveReporter',
  TeamLead = 'TeamLead',
  TalkdeskAgent = 'TalkdeskAgent',
  LeadQueueAgent = 'LeadQueueAgent',
  HeatSalesAgent = 'HeatSalesAgent',
  SalesCall1Agent = 'SalesCall1Agent',
  LeadQueue = 'LeadQueue',
  SpecialSalesChannel1Agent = 'SpecialSalesChannel1Agent',
  SpecialSalesChannel2Agent = 'SpecialSalesChannel2Agent',
  SpecialSalesChannel3Agent = 'SpecialSalesChannel3Agent',
  SpecialSalesChannel4Agent = 'SpecialSalesChannel4Agent',
  SpecialSalesChannel5Agent = 'SpecialSalesChannel5Agent',
  AutodialerTesters = 'AutodialerTesters',
  // Requires App Role "ContactEditor" registered in enpal-cust-sa-dev/prd-stapp-sp and assigned to the "BookingOS Contact Editing" Entra group
  ContactEditor = 'ContactEditor'
}

/**
 * First matching role wins when setting default sales channel on the inbound header.
 * Order matches CurrentUserService.GetSalesChannelFromRoles (revision and CRM before special queues, then
 * sales/field, then SalesCall1Agent before HeatSalesAgent, then lead queue).
 */
export const DEFAULT_SALES_CHANNEL_BY_ROLE_PRIORITY: readonly { role: Role; channel: SalesChannel }[] = [
  { role: Role.RevisionAgent, channel: SalesChannel.Revision },
  { role: Role.CrmSalesRole, channel: SalesChannel.CrmSales },
  { role: Role.SpecialSalesChannel1Agent, channel: SalesChannel.SpecialSalesChannel1 },
  { role: Role.SpecialSalesChannel2Agent, channel: SalesChannel.SpecialSalesChannel2 },
  { role: Role.SpecialSalesChannel3Agent, channel: SalesChannel.SpecialSalesChannel3 },
  { role: Role.SpecialSalesChannel4Agent, channel: SalesChannel.SpecialSalesChannel4 },
  { role: Role.SpecialSalesChannel5Agent, channel: SalesChannel.SpecialSalesChannel5 },
  { role: Role.SalesOSBookingAgent, channel: SalesChannel.SalesOS },
  { role: Role.FieldSalesBookingAgent, channel: SalesChannel.FieldSales },
  // SalesCall1Agent holds the Heat Sales agent role set but must land on SalesCall1Lead, so it is
  // checked before HeatSalesAgent.
  { role: Role.SalesCall1Agent, channel: SalesChannel.SalesCall1Lead },
  { role: Role.HeatSalesAgent, channel: SalesChannel.HeatSales },
  { role: Role.LeadQueueAgent, channel: SalesChannel.LeadQueue },
  { role: Role.LeadQueue, channel: SalesChannel.LeadQueue },
];

export const HasRole = (accounts: AccountInfo[], role: Role) => {
  if (!accounts || accounts.length < 1) {
    return false;
  }

  const userRoles = accounts[0].idTokenClaims?.roles;
  if (!userRoles) {
    return false;
  }

  return userRoles.find((r) => r.toLowerCase() === role.toLowerCase()) !== undefined;
};
